import React, { useState, useEffect, useRef } from 'react';
import { TherapistAboutProps } from '../../types';

/**
 * Componente Testimonials renovado con diseño vanguardista
 */
const TherapistTestimonials: React.FC<TherapistAboutProps> = ({ therapist }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const testimonials = therapist.media.testimonials;
  const sectionRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Configurar el Intersection Observer para animaciones scroll-triggered
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Observar todos los elementos animables
    const animatableElements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    animatableElements?.forEach(el => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  // Auto-carousel effect
  useEffect(() => {
    if (testimonials.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };


  const getStarGradient = (rating: number) => {
    if (rating >= 4.5) return 'linear-gradient(135deg, #ffd700, #ffb300)';
    if (rating >= 4) return 'linear-gradient(135deg, #1E4A8F, #3A72C8)';
    return 'linear-gradient(135deg, #E0A44A, #B8833A)';
  };

  const generateWhatsAppMessage = () => {
    const message = `Hola, vi los testimonios de tus pacientes y me interesa agendar una primera consulta. ¿Podrías brindarme información sobre disponibilidad de horarios y modalidades de atención? Gracias.`;
    const phoneNumber = "56978009119";
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  return (
    <section ref={sectionRef} id="testimonials" className="therapist-testimonials-modern">
      <div className="container">
        {/* Header moderno */}
        <div className="modern-section-header animate-on-scroll">
          <div className="header-decoration"></div>
          <h2 className="modern-section-title">
            Lo que <span className="title-highlight">Dicen</span> Nuestros Pacientes
          </h2>
          <p className="modern-section-subtitle">
            Experiencias reales de personas que han transformado sus vidas con {therapist.name.split(' ')[0]}
          </p>
        </div>

        {/* Métricas modernas */}
        <div className="testimonials-metrics-modern animate-on-scroll">
          <div className="metric-card-modern rating-card">
            <div className="metric-icon-container">
              <div className="metric-icon">
                <i className="fas fa-star"></i>
              </div>
              <div className="metric-glow"></div>
            </div>
            <div className="metric-content">
              <div className="metric-number">5.0</div>
              <div className="metric-label">Rating Promedio</div>
              <div className="metric-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="fas fa-star star-filled"></i>
                ))}
              </div>
            </div>
          </div>

          <div className="metric-card-modern verified-card">
            <div className="metric-icon-container">
              <div className="metric-icon verified-icon">
                <i className="fas fa-certificate"></i>
              </div>
              <div className="metric-glow verified-glow"></div>
            </div>
            <div className="metric-content">
              <div className="metric-number">{testimonials.length}</div>
              <div className="metric-label">Testimonios Verificados</div>
              <div className="metric-badge verified-badge">
                <i className="fas fa-shield-check"></i>
                100% Auténticos
              </div>
            </div>
          </div>

          <div className="metric-card-modern satisfaction-card">
            <div className="metric-icon-container">
              <div className="metric-icon">
                <i className="fas fa-heart"></i>
              </div>
              <div className="metric-glow"></div>
            </div>
            <div className="metric-content">
              <div className="metric-number">98%</div>
              <div className="metric-label">Pacientes Satisfechos</div>
              <div className="metric-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '98%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Carrusel moderno */}
        <div className="testimonials-showcase-modern animate-on-scroll">
          <div className="showcase-container">
            <button 
              className="nav-button-modern prev"
              onClick={prevTestimonial}
              aria-label="Testimonio anterior"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            <div className="testimonial-card-modern">
              <div className="testimonial-background">
                <div className="testimonial-pattern"></div>
              </div>
              
              <div className="testimonial-main-content">
                <div className="quote-container">
                  <div className="quote-decoration"></div>
                  <blockquote className="testimonial-text">
                    "{testimonials[currentTestimonial].content}"
                  </blockquote>
                </div>
                
                <div className="testimonial-rating-modern">
                  <div className="stars-container">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i 
                        key={star} 
                        className={`fas fa-star rating-star ${star <= testimonials[currentTestimonial].rating ? 'filled' : ''}`}
                        style={{ '--star-gradient': getStarGradient(testimonials[currentTestimonial].rating) } as React.CSSProperties}
                      ></i>
                    ))}
                  </div>
                  <span className="rating-text">Calificación: {testimonials[currentTestimonial].rating}/5</span>
                </div>
                
                <div className="testimonial-footer-modern">
                  <div className="patient-info-modern">
                    <div className="patient-avatar-modern">
                      <span>{testimonials[currentTestimonial].patientInitials}</span>
                      <div className="avatar-glow"></div>
                    </div>
                    <div className="patient-details-modern">
                      <div className="patient-name">
                        {testimonials[currentTestimonial].patientInitials}
                      </div>
                      <div className="treatment-category">
                        {testimonials[currentTestimonial].category}
                      </div>
                      <div className="testimonial-date-modern">
                        {new Date(testimonials[currentTestimonial].date).toLocaleDateString('es-CL', {
                          year: 'numeric',
                          month: 'long'
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {testimonials[currentTestimonial].verified && (
                    <div className="verified-badge-modern">
                      <i className="fas fa-certificate"></i>
                      <span>Verificado</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button 
              className="nav-button-modern next"
              onClick={nextTestimonial}
              aria-label="Testimonio siguiente"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          <div className="testimonial-indicators-modern">
            <div className="indicator-progress-bar">
              <div 
                className="indicator-progress-fill"
                style={{ 
                  width: `${((currentTestimonial + 1) / testimonials.length) * 100}%` 
                }}
              ></div>
            </div>
            <div className="indicator-counter">
              <span className="current-number">{currentTestimonial + 1}</span>
              <span className="indicator-separator">/</span>
              <span className="total-number">{testimonials.length}</span>
            </div>
          </div>
        </div>

        {/* Categorías interactivas */}
        <div className="testimonials-categories-modern animate-on-scroll">
          <div className="categories-header-modern">
            <h3 className="categories-title">Testimonios por Especialidad</h3>
            <p className="categories-subtitle">Resultados comprobados en cada área de tratamiento</p>
          </div>
          
          <div className="categories-grid-modern">
            {Array.from(new Set(testimonials.map(t => t.category).filter(Boolean))).map((category, index) => {
              if (!category) return null;
              
              const categoryTestimonials = testimonials.filter(t => t.category === category);
              const avgRating = categoryTestimonials.reduce((sum, t) => sum + t.rating, 0) / categoryTestimonials.length;
              const isSelected = selectedCategory === category;
              
              return (
                <div 
                  key={index} 
                  className={`category-card-modern ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category as string)}
                  style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}
                >
                  <div className="category-card-background">
                    <div className="category-gradient" style={{ 
                      background: getStarGradient(avgRating)
                    }}></div>
                  </div>
                  
                  <div className="category-content-modern">
                    <div className="category-header-modern">
                      <h4 className="category-name">{category}</h4>
                      <div className="category-rating-modern">
                        <span className="rating-number-modern">{avgRating.toFixed(1)}</span>
                        <div className="rating-stars-modern">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i 
                              key={star} 
                              className={`fas fa-star category-star ${star <= Math.round(avgRating) ? 'filled' : ''}`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="category-stats-modern">
                      <div className="testimonials-count">
                        <i className="fas fa-comments"></i>
                        <span>{categoryTestimonials.length} testimonios</span>
                      </div>
                      <div className="satisfaction-rate">
                        <i className="fas fa-thumbs-up"></i>
                        <span>100% satisfacción</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA moderno */}
        <div className="testimonials-cta-modern animate-on-scroll">
          <div className="cta-background-modern">
            <div className="cta-gradient"></div>
            <div className="cta-pattern"></div>
          </div>
          
          <div className="cta-content-modern">
            <div className="cta-text-section">
              <h3 className="cta-title-modern">
                ¿Listo para <span className="cta-highlight">Comenzar</span> tu Transformación?
              </h3>
              <p className="cta-description-modern">
                Únete a cientos de personas que han encontrado el bienestar emocional 
                con el acompañamiento profesional de {therapist.name.split(' ')[0]}.
              </p>
            </div>
            
            <div className="cta-buttons-modern">
              <button className="btn-cta-modern primary">
                <div className="btn-content">
                  <i className="fas fa-calendar-plus"></i>
                  <span>Agendar Primera Consulta</span>
                </div>
                <div className="btn-glow"></div>
              </button>
              
              <a 
                href={generateWhatsAppMessage()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cta-modern secondary"
              >
                <div className="btn-content">
                  <i className="fab fa-whatsapp"></i>
                  <span>Resolver Dudas</span>
                </div>
                <div className="btn-glow"></div>
              </a>
            </div>
          </div>
          
          <div className="cta-floating-elements">
            <div className="floating-element element-1">
              <i className="fas fa-heart"></i>
            </div>
            <div className="floating-element element-2">
              <i className="fas fa-star"></i>
            </div>
            <div className="floating-element element-3">
              <i className="fas fa-smile"></i>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TherapistTestimonials;